import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDatabase,
  faPlus,
  faCheck,
  faXmark,
  faSort,
  faSortUp,
  faSortDown,
  faDownload,
  faSearch,
  faInfoCircle,
  faSpinner,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import ActionButton from "../ui/ActionButton";
import LastUpdatedBadge from "../ui/LastUpdatedBadge";
import LoadingSpinner from "../ui/LoadingSpinner";
import { useDataCache, CACHE_KEYS } from "../../contexts/DataCacheContext";
import {
  fetchMaterials,
  createMaterialEntry,
  updateMaterialEntry,
  toggleMaterialActive,
} from "../../api/adminApi";
import { exportToCSV } from "../../utils/exportUtils";

const EMPTY_MATERIAL = {
  name: "",
  material_category: "",
  emission_factor: "",
  recycling_emissions: "",
  emission_unit: "kgCO2e/kg",
  energy_consumption_kwh: "",
  waste_stage_qty: "",
  reference_price_per_lb: "",
  system_cost_per_lb: "",
  labor_cost_per_lb: "",
};

const hasFormChanges = (original, current) => {
  if (!original || !current) return false;
  return JSON.stringify(original) !== JSON.stringify(current);
};

// format number for display, keep empty if null
const fmtNum = (v) => (v === null || v === undefined ? "" : String(v));

// smart display formatter — rounds and adds thousands separators
const displayNum = (v, decimals = 3) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  if (isNaN(n)) return null;
  // drop trailing zeros: 4.150 → 4.15, 67.000 → 67
  const rounded = parseFloat(n.toFixed(decimals));
  return rounded.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

function MaterialDataSection({ darkMode, t }) {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [editingMat, setEditingMat] = useState(null);
  const [originalEditingMat, setOriginalEditingMat] = useState(null);
  const [newMat, setNewMat] = useState({ ...EMPTY_MATERIAL });
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("active");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null); // name of material being toggled
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // caching
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const { getCachedData, setCachedData } = useDataCache();
  const isFetching = useRef(false);

  const detailPanelRef = useRef(null);
  const sectionTopRef = useRef(null);
  const [toastFading, setToastFading] = useState(false);

  useEffect(() => {
    if (snackbar.open) {
      const fadeTimer = setTimeout(() => setToastFading(true), 2500);
      const closeTimer = setTimeout(() => {
        setSnackbar((s) => ({ ...s, open: false }));
        setToastFading(false);
      }, 3000);
      return () => { clearTimeout(fadeTimer); clearTimeout(closeTimer); };
    }
  }, [snackbar.open]);

  const showToast = (message, severity = "success") => {
    setToastFading(false);
    setSnackbar({ open: true, message, severity });
  };

  // load data with caching
  const loadData = useCallback(async (forceRefresh = false) => {
    if (isFetching.current) return;

    if (!forceRefresh) {
      const cached = getCachedData(CACHE_KEYS.ADMIN_MATERIALS);
      if (cached?.data) {
        setMaterials(cached.data.materials || []);
        setLastUpdated(cached.timestamp);
        setLoading(false);
        return;
      }
    }

    isFetching.current = true;
    try {
      if (forceRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const result = await fetchMaterials();
      const timestamp = Date.now();

      setMaterials(result.materials || []);
      setLastUpdated(timestamp);
      setCachedData(CACHE_KEYS.ADMIN_MATERIALS, result, timestamp);
    } catch (err) {
      console.error("Failed to load materials:", err);
      setError(t("Failed to load materials"));
    } finally {
      setLoading(false);
      setRefreshing(false);
      isFetching.current = false;
    }
  }, [t, getCachedData, setCachedData]);

  const handleRefresh = useCallback(() => loadData(true), [loadData]);

  useEffect(() => { loadData(false); }, [loadData]);

  const scrollToDetailPanel = () => {
    setTimeout(() => {
      detailPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  // unique categories for filter dropdown
  const categories = useMemo(() => {
    const cats = [...new Set(materials.map((m) => m.material_category).filter(Boolean))];
    return cats.sort();
  }, [materials]);

  // filtered + sorted
  const filteredMaterials = useMemo(() => {
    let result = materials.filter((m) =>
      m.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    if (filterCategory !== "all") {
      result = result.filter((m) => m.material_category === filterCategory);
    }

    if (filterStatus === "active") {
      result = result.filter((m) => m.active !== false);
    } else if (filterStatus === "disabled") {
      result = result.filter((m) => m.active === false);
    }

    result.sort((a, b) => {
      let aVal = a[sortConfig.key] ?? "";
      let bVal = b[sortConfig.key] ?? "";

      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [materials, searchQuery, sortConfig, filterCategory, filterStatus]);

  // reset to page 1 when filters change
  useEffect(() => setCurrentPage(1), [searchQuery, filterCategory, filterStatus, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(filteredMaterials.length / itemsPerPage));
  const paginated = filteredMaterials.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  const handleEdit = (mat) => {
    setEditingMat({ ...mat });
    setOriginalEditingMat({ ...mat });
    setShowCreatePanel(false);
    scrollToDetailPanel();
  };

  const handleCreate = async () => {
    if (!newMat.name.trim()) return;

    setSaving(true);
    try {
      await createMaterialEntry(newMat);
      showToast(t("Material created"));
      setNewMat({ ...EMPTY_MATERIAL });
      setShowCreatePanel(false);
      await loadData(true);
    } catch (err) {
      showToast(err.message || t("Failed to create material"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingMat?.name) return;

    setSaving(true);
    try {
      await updateMaterialEntry(editingMat);
      showToast(t("Material updated"));

      // update local state
      setMaterials((prev) =>
        prev.map((m) => (m.name === editingMat.name ? { ...editingMat } : m)),
      );
      setOriginalEditingMat({ ...editingMat });

      // refresh cache
      const timestamp = Date.now();
      const updated = materials.map((m) =>
        m.name === editingMat.name ? { ...editingMat } : m,
      );
      setCachedData(CACHE_KEYS.ADMIN_MATERIALS, { materials: updated }, timestamp);
      setLastUpdated(timestamp);
    } catch (err) {
      showToast(err.message || t("Failed to update material"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (name, currentActive) => {
    const newActive = !currentActive;
    setToggling(name);
    try {
      await toggleMaterialActive(name, newActive);

      // update local state
      setMaterials((prev) =>
        prev.map((m) => (m.name === name ? { ...m, active: newActive } : m)),
      );

      // update editing panel if this material is open
      if (editingMat?.name === name) {
        setEditingMat((prev) => ({ ...prev, active: newActive }));
        setOriginalEditingMat((prev) => ({ ...prev, active: newActive }));
      }

      // refresh cache
      const timestamp = Date.now();
      const updated = materials.map((m) =>
        m.name === name ? { ...m, active: newActive } : m,
      );
      setCachedData(CACHE_KEYS.ADMIN_MATERIALS, { materials: updated }, timestamp);
      setLastUpdated(timestamp);

      showToast(newActive ? t("Material enabled") : t("Material disabled"));
    } catch (err) {
      showToast(err.message || t("Failed to toggle material"), "error");
    } finally {
      setToggling(null);
    }
  };

  const handleExportCSV = () => {
    const data = filteredMaterials.map((m) => ({
      Name: m.name,
      Category: m.material_category,
      "Emission Factor": m.emission_factor ?? "",
      "Recycling Emissions": m.recycling_emissions ?? "",
      "Emission Unit": m.emission_unit,
      "Energy (kWh)": m.energy_consumption_kwh ?? "",
      "Waste Stage Qty": m.waste_stage_qty ?? "",
      Status: m.active !== false ? "Active" : "Disabled",
    }));
    exportToCSV(data, "material-data");
  };

  if (loading && materials.length === 0) {
    return (
      <div className="profile-section admin-section-wide">
        <div className="admin-loading">
          <LoadingSpinner size={40} />
          <span>{t("Loading materials...")}</span>
        </div>
      </div>
    );
  }

  const isEdited = hasFormChanges(originalEditingMat, editingMat);

  return (
    <div className="profile-section admin-section-wide mat-data-section" ref={sectionTopRef}>
      <div className="profile-section-header">
        <div className="profile-section-icon admin-icon">
          <FontAwesomeIcon icon={faDatabase} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="profile-section-title">{t("Material Data")}</h2>
          <p className="profile-section-desc">
            {t("Manage material emission factors, energy consumption and waste data")}
          </p>
        </div>
        <div className="header-status-area">
          {snackbar.open && (
            <div className={`inline-toast ${snackbar.severity} ${toastFading ? "fading" : ""}`}>
              <FontAwesomeIcon icon={snackbar.severity === "error" ? faInfoCircle : faCheck} />
              <span>{snackbar.message}</span>
            </div>
          )}
          <LastUpdatedBadge
            timestamp={lastUpdated}
            onRefresh={handleRefresh}
            isRefreshing={refreshing}
            variant="admin"
          />
        </div>
      </div>

      {error && (
        <div className="admin-error">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{error}</span>
        </div>
      )}

      {/* toolbar */}
      <div className="admin-toolbar">
        <div className="admin-toolbar-left">
          <div className="admin-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input"
              placeholder={t("Search materials...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="admin-filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">{t("All Categories")}</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select
            className="admin-filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">{t("All Status")}</option>
            <option value="active">{t("Active")}</option>
            <option value="disabled">{t("Disabled")}</option>
          </select>
        </div>
        <div className="admin-toolbar-right">
          <button
            className="admin-toolbar-btn"
            onClick={handleExportCSV}
            title={t("Export CSV")}
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            className="admin-toolbar-btn admin-primary"
            onClick={() => {
              setShowCreatePanel(true);
              setEditingMat(null);
              setOriginalEditingMat(null);
              scrollToDetailPanel();
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{t("New Material")}</span>
          </button>
        </div>
      </div>

      {/* table */}
      <div className={`admin-table-container ${refreshing ? "shimmer-overlay" : ""}`}>
        <table className="admin-table material-data-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort("name")}>
                {t("Name")}
                <Tooltip title={t("Material name used to match listings")} arrow placement="top">
                  <span className="th-info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                </Tooltip>
                <FontAwesomeIcon icon={getSortIcon("name")} className="sort-icon" />
              </th>
              <th className="sortable" onClick={() => handleSort("material_category")}>
                {t("Category")}
                <Tooltip title={t("Grouping used for filtering and reporting")} arrow placement="top">
                  <span className="th-info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                </Tooltip>
                <FontAwesomeIcon icon={getSortIcon("material_category")} className="sort-icon" />
              </th>
              <th className="sortable" onClick={() => handleSort("emission_factor")}>
                {t("Emissions")}
                <span className="th-unit">(kgCO2e/kg)</span>
                <Tooltip title={t("CO₂ equivalent emitted per kg of material produced")} arrow placement="top">
                  <span className="th-info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                </Tooltip>
                <FontAwesomeIcon icon={getSortIcon("emission_factor")} className="sort-icon" />
              </th>
              <th className="sortable" onClick={() => handleSort("recycling_emissions")}>
                {t("Recycling")}
                <span className="th-unit">(kgCO2e/kg)</span>
                <Tooltip title={t("CO₂ equivalent emitted per kg during recycling")} arrow placement="top">
                  <span className="th-info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                </Tooltip>
                <FontAwesomeIcon icon={getSortIcon("recycling_emissions")} className="sort-icon" />
              </th>
              <th className="sortable" onClick={() => handleSort("energy_consumption_kwh")}>
                {t("Energy")}
                <span className="th-unit">(kWh/kg)</span>
                <Tooltip title={t("Energy consumed per kg of material processed")} arrow placement="top">
                  <span className="th-info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                </Tooltip>
                <FontAwesomeIcon icon={getSortIcon("energy_consumption_kwh")} className="sort-icon" />
              </th>
              <th className="sortable" onClick={() => handleSort("waste_stage_qty")}>
                {t("Waste")}
                <span className="th-unit">(kg)</span>
                <Tooltip title={t("Waste generated per unit at disposal stage")} arrow placement="top">
                  <span className="th-info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                </Tooltip>
                <FontAwesomeIcon icon={getSortIcon("waste_stage_qty")} className="sort-icon" />
              </th>
              <th className="sortable" onClick={() => handleSort("total_listings")}>
                {t("Listings")}
                <Tooltip title={t("Number of active listings using this material and delivered requests")} arrow placement="top">
                  <span className="th-info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                </Tooltip>
                <FontAwesomeIcon icon={getSortIcon("total_listings")} className="sort-icon" />
              </th>
              <th className="sortable" onClick={() => handleSort("active")}>
                {t("Status")}
                <Tooltip title={t("Disabled materials are hidden from donor/receiver dropdowns")} arrow placement="top">
                  <span className="th-info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                </Tooltip>
                <FontAwesomeIcon icon={getSortIcon("active")} className="sort-icon" />
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((mat) => {
              const isEditing = editingMat?.name === mat.name;
              const total = mat.total_listings || 0;
              const delivered = mat.delivered_requests || 0;
              const isActive = mat.active !== false;
              const isToggling = toggling === mat.name;

              return (
                <tr
                  key={mat.name}
                  className={`${isEditing ? "row-editing" : ""} ${!isActive ? "row-disabled" : ""}`}
                  onClick={() => handleEdit(mat)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <Tooltip title={mat.name} arrow placement="top-start" disableHoverListener={mat.name.length < 20}>
                      <span className="cell-primary mat-name">{mat.name}</span>
                    </Tooltip>
                  </td>
                  <td>
                    {mat.material_category ? (
                      <span className="cell-category-tag">{mat.material_category}</span>
                    ) : (
                      <span className="cell-muted">—</span>
                    )}
                  </td>
                  <td className="cell-numeric">{displayNum(mat.emission_factor, 3) ?? <span className="cell-muted">—</span>}</td>
                  <td className="cell-numeric">{displayNum(mat.recycling_emissions, 3) ?? <span className="cell-muted">—</span>}</td>
                  <td className="cell-numeric">{displayNum(mat.energy_consumption_kwh, 2) ?? <span className="cell-muted">—</span>}</td>
                  <td className="cell-numeric">{displayNum(mat.waste_stage_qty, 2) ?? <span className="cell-muted">—</span>}</td>
                  <td className="cell-numeric">
                    {total > 0 ? (
                      <span className="mat-usage-badge">
                        {total}
                        {delivered > 0 && (
                          <span className="mat-usage-delivered">({delivered} {t("delivered")})</span>
                        )}
                      </span>
                    ) : (
                      <span className="mat-usage-unused">{t("Unused")}</span>
                    )}
                  </td>
                  <td className="td-toggle" onClick={(e) => e.stopPropagation()}>
                    <label className={`mat-toggle ${isToggling ? "toggling" : ""}`}>
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => handleToggleActive(mat.name, isActive)}
                        disabled={isToggling}
                      />
                      <span className="mat-toggle-slider" />
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredMaterials.length === 0 && (
          <div className="admin-empty-state">
            <FontAwesomeIcon icon={faDatabase} />
            <p>{t("No materials match your filters")}</p>
          </div>
        )}
      </div>

      {/* table footer with pagination */}
      <div className="admin-table-footer">
        <span className="table-count">
          {t("Showing")}{" "}
          {Math.min((currentPage - 1) * itemsPerPage + 1, filteredMaterials.length)}
          –{Math.min(currentPage * itemsPerPage, filteredMaterials.length)}{" "}
          {t("of")} {filteredMaterials.length}
        </span>
        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => {
                setCurrentPage((p) => Math.max(1, p - 1));
                sectionTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              disabled={currentPage === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="pagination-info">
              {currentPage} / {totalPages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => {
                setCurrentPage((p) => Math.min(totalPages, p + 1));
                sectionTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              disabled={currentPage === totalPages}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      {/* create panel */}
      {showCreatePanel && (
        <div
          className="admin-detail-panel"
          ref={detailPanelRef}
          style={{ scrollMarginTop: "100px" }}
        >
          <div className="admin-detail-header">
            <div className="admin-detail-title">
              <div className="user-avatar site-admin">
                <FontAwesomeIcon icon={faDatabase} />
              </div>
              <div>
                <h3>{t("New Material")}</h3>
                <span>{t("Add a new material to the database")}</span>
              </div>
            </div>
            <button
              className="admin-detail-close"
              onClick={() => setShowCreatePanel(false)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="admin-detail-body">
            <div className="admin-detail-grid">
              <div className="admin-detail-field">
                <label>{t("Material Name")} *</label>
                <input
                  type="text"
                  className="profile-input"
                  value={newMat.name}
                  onChange={(e) => setNewMat({ ...newMat, name: e.target.value })}
                  placeholder={t("e.g. Cardboard")}
                />
              </div>
              <div className="admin-detail-field">
                <label>{t("Category")}</label>
                <select
                  className="profile-input"
                  value={newMat.material_category}
                  onChange={(e) => setNewMat({ ...newMat, material_category: e.target.value })}
                >
                  <option value="">{t("Select a category")}</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="admin-detail-field">
                <label>{t("Emission Factor")}</label>
                <input
                  type="number"
                  step="any"
                  className="profile-input mat-num-input"
                  value={newMat.emission_factor}
                  onChange={(e) => setNewMat({ ...newMat, emission_factor: e.target.value })}
                  placeholder="0.0"
                />
              </div>
              <div className="admin-detail-field">
                <label>{t("Recycling Emissions")}</label>
                <input
                  type="number"
                  step="any"
                  className="profile-input mat-num-input"
                  value={newMat.recycling_emissions}
                  onChange={(e) => setNewMat({ ...newMat, recycling_emissions: e.target.value })}
                  placeholder="0.0"
                />
              </div>
              <div className="admin-detail-field">
                <label>{t("Energy Consumption (kWh)")}</label>
                <input
                  type="number"
                  step="any"
                  className="profile-input mat-num-input"
                  value={newMat.energy_consumption_kwh}
                  onChange={(e) => setNewMat({ ...newMat, energy_consumption_kwh: e.target.value })}
                  placeholder="0.0"
                />
              </div>
              <div className="admin-detail-field">
                <label>{t("Waste Stage Qty")}</label>
                <input
                  type="number"
                  step="any"
                  className="profile-input mat-num-input"
                  value={newMat.waste_stage_qty}
                  onChange={(e) => setNewMat({ ...newMat, waste_stage_qty: e.target.value })}
                  placeholder="0.0"
                />
              </div>
            </div>

            <div className="admin-subsection">
              <label className="admin-subsection-label">
                {t("MFCA Reference Costs")}
                <Tooltip
                  title={t("Reference per-lb values used as fallbacks for MFCA calculations when donors don't enter an acquisition cost on a listing. All optional.")}
                  placement="top"
                  arrow
                >
                  <span className="admin-info-icon">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </Tooltip>
              </label>
              <div className="admin-detail-grid">
                <div className="admin-detail-field">
                  <label>{t("Reference Price")}</label>
                  <input
                    type="number"
                    step="any"
                    className="profile-input mat-num-input"
                    value={newMat.reference_price_per_lb}
                    onChange={(e) => setNewMat({ ...newMat, reference_price_per_lb: e.target.value })}
                    placeholder="CAD/lb"
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("System Cost")}</label>
                  <input
                    type="number"
                    step="any"
                    className="profile-input mat-num-input"
                    value={newMat.system_cost_per_lb}
                    onChange={(e) => setNewMat({ ...newMat, system_cost_per_lb: e.target.value })}
                    placeholder="CAD/lb"
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("Labor Cost")}</label>
                  <input
                    type="number"
                    step="any"
                    className="profile-input mat-num-input"
                    value={newMat.labor_cost_per_lb}
                    onChange={(e) => setNewMat({ ...newMat, labor_cost_per_lb: e.target.value })}
                    placeholder="CAD/lb"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="admin-detail-footer">
            <ActionButton onClick={() => setShowCreatePanel(false)} variant="default">
              {t("Cancel")}
            </ActionButton>
            <ActionButton
              onClick={handleCreate}
              variant="secondary"
              disabled={!newMat.name.trim() || saving}
            >
              {saving ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> {t("Creating...")}</>
              ) : (
                t("Create")
              )}
            </ActionButton>
          </div>
        </div>
      )}

      {/* edit panel */}
      {editingMat && !showCreatePanel && (() => {
        const total = editingMat.total_listings || 0;
        const delivered = editingMat.delivered_requests || 0;
        const hasUsage = total > 0;

        return (
        <div
          className="admin-detail-panel"
          ref={detailPanelRef}
          style={{ scrollMarginTop: "100px" }}
        >
          <div className="admin-detail-header">
            <div className="admin-detail-title">
              <div className="user-avatar site-admin">
                <FontAwesomeIcon icon={faDatabase} />
              </div>
              <div>
                <h3>
                  {editingMat.name}
                  {hasUsage && (
                    <Tooltip
                      title={
                        `${t("Used by")} ${total} ${total === 1 ? t("listing") : t("listings")}` +
                        (delivered > 0 ? ` (${delivered} ${t("delivered")})` : "") +
                        `. ${t("Changes will affect GHG calculations.")}`
                      }
                      arrow
                      placement="right"
                    >
                      <span className={`mat-usage-icon ${delivered > 0 ? "has-delivered" : ""}`}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </span>
                    </Tooltip>
                  )}
                </h3>
                <span>
                  {editingMat.material_category || t("No category")}
                  {editingMat.emission_unit && editingMat.emission_unit !== "kgCO2e/kg" && (
                    <> · {editingMat.emission_unit}</>
                  )}
                </span>
              </div>
            </div>
            <button
              className="admin-detail-close"
              onClick={() => { setEditingMat(null); setOriginalEditingMat(null); }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="admin-detail-body">
            <div className="admin-detail-grid">
              <div className="admin-detail-field">
                <label>{t("Emission Factor")} <span className="th-unit">(kgCO2e/kg)</span></label>
                <input
                  type="number"
                  step="any"
                  className="profile-input mat-num-input"
                  value={fmtNum(editingMat.emission_factor)}
                  onChange={(e) => setEditingMat({ ...editingMat, emission_factor: e.target.value === "" ? null : parseFloat(e.target.value) })}
                />
              </div>
              <div className="admin-detail-field">
                <label>{t("Recycling Emissions")} <span className="th-unit">(kgCO2e/kg)</span></label>
                <input
                  type="number"
                  step="any"
                  className="profile-input mat-num-input"
                  value={fmtNum(editingMat.recycling_emissions)}
                  onChange={(e) => setEditingMat({ ...editingMat, recycling_emissions: e.target.value === "" ? null : parseFloat(e.target.value) })}
                />
              </div>
              <div className="admin-detail-field">
                <label>{t("Energy Consumption")} <span className="th-unit">(kWh/kg)</span></label>
                <input
                  type="number"
                  step="any"
                  className="profile-input mat-num-input"
                  value={fmtNum(editingMat.energy_consumption_kwh)}
                  onChange={(e) => setEditingMat({ ...editingMat, energy_consumption_kwh: e.target.value === "" ? null : parseFloat(e.target.value) })}
                />
              </div>
              <div className="admin-detail-field">
                <label>{t("Waste Stage Qty")} <span className="th-unit">(kg)</span></label>
                <input
                  type="number"
                  step="any"
                  className="profile-input mat-num-input"
                  value={fmtNum(editingMat.waste_stage_qty)}
                  onChange={(e) => setEditingMat({ ...editingMat, waste_stage_qty: e.target.value === "" ? null : parseFloat(e.target.value) })}
                />
              </div>
            </div>

            <div className="admin-subsection">
              <label className="admin-subsection-label">
                {t("MFCA Reference Costs")}
                <Tooltip
                  title={t("Reference per-lb values used as fallbacks for MFCA calculations when donors don't enter an acquisition cost on a listing. All optional.")}
                  placement="top"
                  arrow
                >
                  <span className="admin-info-icon">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </Tooltip>
              </label>
              <div className="admin-detail-grid">
                <div className="admin-detail-field">
                  <label>{t("Reference Price")} <span className="th-unit">(CAD/lb)</span></label>
                  <input
                    type="number"
                    step="any"
                    className="profile-input mat-num-input"
                    value={fmtNum(editingMat.reference_price_per_lb)}
                    onChange={(e) => setEditingMat({ ...editingMat, reference_price_per_lb: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("System Cost")} <span className="th-unit">(CAD/lb)</span></label>
                  <input
                    type="number"
                    step="any"
                    className="profile-input mat-num-input"
                    value={fmtNum(editingMat.system_cost_per_lb)}
                    onChange={(e) => setEditingMat({ ...editingMat, system_cost_per_lb: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("Labor Cost")} <span className="th-unit">(CAD/lb)</span></label>
                  <input
                    type="number"
                    step="any"
                    className="profile-input mat-num-input"
                    value={fmtNum(editingMat.labor_cost_per_lb)}
                    onChange={(e) => setEditingMat({ ...editingMat, labor_cost_per_lb: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="admin-detail-footer">
            <ActionButton
              onClick={() => { setEditingMat(null); setOriginalEditingMat(null); }}
              variant="default"
            >
              {t("Cancel")}
            </ActionButton>
            {isEdited && (
              <ActionButton onClick={handleSaveEdit} variant="secondary" disabled={saving}>
                {saving ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> {t("Saving...")}</>
                ) : (
                  t("Save Changes")
                )}
              </ActionButton>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
}

export default MaterialDataSection;
